import React, { useState, useContext } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  FormFeedback,
  InputGroup,
  InputGroupText,
  Spinner,
} from "reactstrap";
import { FaEye, FaEyeSlash, FaEnvelope, FaLock } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { call, put, takeEvery } from "redux-saga/effects";
import axios from "axios";

import '../../../../assets/FrontEnd/css/font.css';
import '../../../../assets/FrontEnd/plugins/bootstrap/css/bootstrap.min.css';
import '../../../../assets/FrontEnd/css/style.css';
import { AuthContext } from "../../../../constants/AuthContext";

const FrontPageLogin = () => {
  const navigate = useNavigate();
  const { setAuthUser } = useContext(AuthContext);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState("");

  const [touchedEmail, setTouchedEmail] = useState(false);
  const [touchedPassword, setTouchedPassword] = useState(false);

  const emailRegex =
    /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
  const isEmailValid = emailRegex.test(email.trim());
  const isPasswordValid = password.trim().length >= 6;

  const formValid = isEmailValid && isPasswordValid;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouchedEmail(true);
    setTouchedPassword(true);
    setServerError("");

    if (!formValid) return;

    setSubmitting(true);
    try {
      let formData = new FormData();
      formData.append('email', email.trim());
      formData.append('password', password);

      const response = await axios.post(
        process.env.REACT_APP_WEB_APP_URL + "/api/web-login",
        formData
      );

      if (response.status != 200) {
        setServerError(response?.data?.message || "Login failed. Check credentials.");
        setSubmitting(false);
        return;
      }

      const { token, user } = response.data.data;

      // localStorage.setItem("authClient", JSON.stringify(user));
      setAuthUser(user);
      localStorage.setItem("authToken", token);
      localStorage.setItem("isProfileLocked", false);
      localStorage.setItem("authUserPassword", password);

      navigate("/");
    } catch (err) {
      setServerError(err?.response?.data?.error || "Network error. Please try again.");
      setSubmitting(false);
    }
  };

  document.title = "Login | Sawari Online";
  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col xs="12" sm="10" md="8" lg="6" xl="5">
          <Card className="shadow-sm">
            <CardBody className="p-4">
              <h3 className="mb-3 text-center">Welcome back</h3>
              <p className="text-center text-muted mb-4">Sign in to continue to your account</p>

              {serverError && (
                <div className="alert alert-danger py-2" role="alert">
                  {serverError}
                </div>
              )}

              <Form onSubmit={handleSubmit} noValidate>
                {/* Email */}
                <FormGroup className="mb-3">
                  <Label for="email" className="form-label">
                    Email
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <FaEnvelope />
                    </InputGroupText>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onBlur={() => setTouchedEmail(true)}
                      invalid={touchedEmail && !isEmailValid}
                      aria-describedby="emailHelp"
                      autoComplete="email"
                    />
                    <FormFeedback valid={false}>Please enter a valid email address.</FormFeedback>
                  </InputGroup>
                </FormGroup>

                {/* Password */}
                <FormGroup className="mb-3">
                  <Label for="password" className="form-label">
                    Password
                  </Label>
                  <InputGroup>
                    <InputGroupText>
                      <FaLock />
                    </InputGroupText>
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onBlur={() => setTouchedPassword(true)}
                      invalid={touchedPassword && !isPasswordValid}
                      autoComplete="current-password"
                    />
                    <InputGroupText
                      style={{ cursor: "pointer" }}
                      onClick={() => setShowPassword((s) => !s)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </InputGroupText>
                    <FormFeedback valid={false}>
                      Password must be at least 6 characters.
                    </FormFeedback>
                  </InputGroup>
                </FormGroup>

                {/* Remember + Forgot */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="form-check">
                    <input
                      id="remember"
                      type="checkbox"
                      className="form-check-input"
                      checked={remember}
                      onChange={(e) => setRemember(e.target.checked)}
                    />
                    <label htmlFor="remember" className="form-check-label">
                      Remember me
                    </label>
                  </div>
                  <div>
                    <a href="/forgot-password" className="small">
                      Forgot password?
                    </a>
                  </div>
                </div>

                {/* Submit */}
                <div className="d-grid mb-3">
                  <Button color="primary" type="submit" disabled={!formValid || submitting}>
                    {submitting ? (
                      <>
                        <Spinner size="sm" /> &nbsp; Signing in...
                      </>
                    ) : (
                      "Sign in"
                    )}
                  </Button>
                </div>

                {/* Divider */}
                <div className="text-center text-muted mb-2">or</div>

                {/* Social / Signup */}
                <div className="d-grid gap-2">
                  <Button color="link" onClick={() => navigate("/register")}>
                    Create an account
                  </Button>
                </div>
              </Form>
            </CardBody>
          </Card>

          {/* subtle footer */}
          <p className="text-center small text-muted mt-3">
            By continuing you agree to our <a href="#">Terms</a> and <a href="#">Privacy</a>.
          </p>
        </Col>
      </Row>
    </Container>
  );
};

export default FrontPageLogin;
